import React, { useMemo, useState } from 'react';
import { useFilters } from '../../contexts/FilterContext';
import { makeCohortScope, resolveTerritoryIds } from '../../utils/filters';
import { ALL_TERRITORY_IDS } from '../../utils/territory';
import { retentionTotal, retentionTrend } from '../../utils/retention';
import { COHORT_SEASONS } from '../../utils/calendar';
import { fmtInt, fmtPct, fmtPP } from '../../utils/format';
import { Panel } from '../ui/Panel';
import { StatCard } from '../ui/StatCard';
import { EmptyState } from '../ui/EmptyState';
import { RetentionControls, type RetentionMode, type YoyWindow } from './RetentionControls';
import { RetentionTrendChart } from './RetentionTrendChart';
import { RetentionBreakdown } from './RetentionBreakdown';
import { DedupExample } from './DedupExample';
interface RetentionViewProps {
  /** Optional drilldown scope; otherwise the global territory / AOP filters apply. */
  territoryIds?: string[];
  aopIds?: string[];
  entityLabel?: string;
}
export function RetentionView({
  territoryIds: forcedTerritories,
  aopIds: forcedAops,
  entityLabel: forcedLabel
}: RetentionViewProps) {
  const {
    filters,
    reset
  } = useFilters();
  const [mode, setMode] = useState<RetentionMode>('season');
  const [seasonBase, setSeasonBase] = useState(9);
  const [seasonComp, setSeasonComp] = useState(10);
  const [yoyBase, setYoyBase] = useState(6);
  const [yoyWindow, setYoyWindow] = useState<YoyWindow>('sameSeason');
  const territoryIds = useMemo(() => forcedTerritories ?? resolveTerritoryIds(filters), [filters, forcedTerritories]);
  const aopIds = forcedAops ?? filters.aops;
  const scope = useMemo(() => makeCohortScope(filters, territoryIds, aopIds), [filters, territoryIds, aopIds]);
  const network = useMemo(() => makeCohortScope(filters, ALL_TERRITORY_IDS, null), [filters]);
  const within12 = mode === 'yoy' && yoyWindow === 'within12';
  const base = mode === 'season' ? seasonBase : yoyBase;
  const comp = mode === 'season' ? seasonComp : yoyBase + 4;
  const gap = comp - base;
  const changeBase = (v: number) => {
    if (mode === 'season') {
      setSeasonBase(v);
      if (seasonComp <= v) setSeasonComp(v + 1);
    } else setYoyBase(v);
  };
  const result = useMemo(() => {
    const cur = retentionTotal(scope, base, comp, within12);
    const net = retentionTotal(network, base, comp, within12);
    const prior = base - 4 >= 0 ? retentionTotal(scope, base - 4, comp - 4, within12) : null;
    const trend = retentionTrend(scope, network, gap, within12);
    return {
      cur,
      net,
      prior,
      trend
    };
  }, [scope, network, base, comp, gap, within12]);
  const pairLabel = within12 ? `${COHORT_SEASONS[base].label} → any program within 12 months` : `${COHORT_SEASONS[base].label} → ${COHORT_SEASONS[comp].label}`;
  const priorLabel = base - 4 >= 0 ? within12 ? `${COHORT_SEASONS[base - 4].short} + 12 mo` : `${COHORT_SEASONS[base - 4].short} → ${COHORT_SEASONS[comp - 4].short}` : null;
  const entityLabel = forcedLabel ?? (territoryIds.length === ALL_TERRITORY_IDS.length && aopIds.length === 0 ? 'All territories' : 'Selected scope');
  const {
    cur,
    net,
    prior
  } = result;
  const yoyChange = prior && prior.base ? cur.rate - prior.rate : null;
  if (territoryIds.length === 0) {
    return <div className="rounded-lg border border-line bg-white" data-id="element-486" data-mp="342wkf4c2u27z">
        <EmptyState title="No territories match these filters" action={<button type="button" onClick={reset} className="text-xs font-semibold text-navy hover:underline" data-id="element-488" data-mp="nis6c3kfino">Reset filters</button>} data-id="element-487" data-mp="394diyt739cle" />
      </div>;
  }
  return <div className="space-y-5" data-id="element-489" data-mp="5l0yrvu41lsi">
      <RetentionControls mode={mode} onModeChange={setMode} base={base} onBaseChange={changeBase} comp={seasonComp} onCompChange={setSeasonComp} yoyWindow={yoyWindow} onYoyWindowChange={setYoyWindow} data-id="element-490" data-mp="3nt9oaihrgbib" />

      {cur.base === 0 ? <div className="rounded-lg border border-line bg-white" data-id="element-491" data-mp="2gfofwomh2x70">
          <EmptyState title="No participants in this base season" description="Territories in scope had not launched yet, or the sport / program filters exclude every participant." data-id="element-492" data-mp="2gctv2vzjrad2" />
        </div> : <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-493" data-mp="2mw8cd5us31gu">
            <StatCard label={mode === 'season' ? 'Season Retention' : 'YOY Retention'} value={fmtPct(cur.rate)} emphasis definitionId={mode === 'season' ? 'seasonRetention' : 'yoyRetention'} sub={<span data-id="element-495" data-mp="3andtdocgfzsq">
                  {pairLabel}
                  <span className={`ml-1.5 font-semibold ${cur.rate - net.rate < 0 ? 'text-negative' : 'text-positive'}`} data-id="element-496" data-mp="35mzm48iogtvr">{fmtPP(cur.rate - net.rate)} vs network</span>
                </span>} data-id="element-494" data-mp="2muf63ro2uj9d" />
            <StatCard label="Base season participants" value={fmtInt(cur.base)} definitionId="seasonRetention" sub={<>From <b className="text-ink" data-id="element-498" data-mp="1q8lm5poasfbu">{fmtInt(cur.regRows)}</b> registrations, each participant counted once</>} data-id="element-497" data-mp="1u8c9tkij2fc5" />
            <StatCard label="Returning participants" value={fmtInt(cur.retained)} sub={within12 ? 'Registered for any program within 12 months' : `Also registered in ${COHORT_SEASONS[comp].label}`} data-id="element-499" data-mp="9pifqv5sv15x" />
            <StatCard label="YoY change" value={yoyChange === null ? 'n/a' : fmtPP(yoyChange)} sub={priorLabel ? `vs ${priorLabel}: ${prior && prior.base ? fmtPct(prior.rate) : 'no cohort'}` : 'No prior-year cohort in sample data'} data-id="element-500" data-mp="jgwdoyi3rswy" />
          </div>

          <div className="grid gap-5 xl:grid-cols-12" data-id="element-501" data-mp="xg5v21i1ot2i">
            <Panel className="xl:col-span-8" title="Retention trend by base season" definitionId={mode === 'season' ? 'seasonRetention' : 'yoyRetention'} subtitle={within12 ? 'Each base season → any program within 12 months' : `Each base season → ${gap === 1 ? 'the following season' : `${gap} seasons later`}`} data-id="element-502" data-mp="12epgm0sq82uc">
              <RetentionTrendChart points={result.trend} selectedBase={base} entityLabel={entityLabel} data-id="element-503" data-mp="6csfjmnpdqpu" />
              {COHORT_SEASONS[comp]?.inProgress && !within12 && <p className="mt-2 text-[11.5px] text-ink-subtle" data-id="element-504" data-mp="k9nrpnz93ya4">{COHORT_SEASONS[comp].label} is in progress, so retention into it may still rise as late registrations arrive.</p>}
            </Panel>
            <Panel className="xl:col-span-4" title="One participant, many registrations" subtitle="How participants are counted once (sample)" data-id="element-505" data-mp="pb4tdlpk7orr">
              <DedupExample data-id="element-506" data-mp="13187tqi76e6n" />
            </Panel>
          </div>

          <RetentionBreakdown scope={scope} network={network} base={base} comp={comp} within12={within12} trend={result.trend} pairLabel={pairLabel} data-id="element-507" data-mp="z0eicsp3wrbn" />
        </>}
    </div>;
}